'use client';

import { c as _c } from "react/compiler-runtime";
import { Trash } from '@sprd/sprd-component-kit/v2';
import { ReactNode, useState } from 'react';
import clsx from 'clsx';
import { useFormatter, useTranslations } from 'next-intl';
import { useCurrency } from '@/hooks/useCurrency';
import { VolumeDiscountTiersDialog } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/components/VolumeDiscountDialogs';
import { getOrderValue } from '@/utility/shippingCosts';
import { VolumeDiscountProductItem } from './VolumeDiscountProductCard';
import { formatShippingDays } from './VolumeDiscountShipping';
import { LinePrice, getNextTier } from './volumeDiscountPricing';
type ShippingMethod = {
  shippingName?: string;
  cost?: number | null;
  minDays?: number;
  maxDays?: number;
};
type VolumeDiscountSummaryProps = {
  items: (VolumeDiscountProductItem & LinePrice & {
    index: number;
  })[];
  shippingMethod?: ShippingMethod;
  isFetching?: boolean;
  onRemove: (index: number, quantity: number) => void;
};
type SummaryRowProps = {
  label: ReactNode;
  value: ReactNode;
  highlight?: boolean;
  freeShipping?: boolean;
  stackOnMobile?: boolean;
};
function SummaryRow(t0) {
  const $ = _c(14);
  const {
    label,
    value,
    highlight,
    freeShipping,
    stackOnMobile
  } = t0;
  const t1 = stackOnMobile && "max-md:flex-col max-md:items-end max-md:gap-1";
  const t2 = highlight ? "font-bold text-red-600" : "text-neutral-800";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = clsx("flex items-center justify-between gap-2 text-sm", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== label) {
    t4 = <dt>{label}</dt>;
    $[3] = label;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = !highlight && (freeShipping ? "font-bold text-green-900" : "text-black");
  let t6;
  if ($[5] !== t5) {
    t6 = clsx("shrink-0 whitespace-nowrap", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6 || $[8] !== value) {
    t7 = <dd className={t6}>{value}</dd>;
    $[7] = t6;
    $[8] = value;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t7) {
    t8 = <div className={t3}>{t4}{t7}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
export default function VolumeDiscountSummary(t0) {
  const $ = _c(88);
  const {
    items,
    shippingMethod,
    isFetching,
    onRemove
  } = t0;
  const {
    data: currency
  } = useCurrency();
  const formatter = useFormatter();
  const t = useTranslations("volumeDiscountCalculator.summary");
  const [tiersIndex, setTiersIndex] = useState();
  let t1;
  if ($[0] !== items || $[1] !== tiersIndex) {
    let t2;
    if ($[3] !== tiersIndex) {
      t2 = item => item.index === tiersIndex;
      $[3] = tiersIndex;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = items.find(t2);
    $[0] = items;
    $[1] = tiersIndex;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const tiersItem = t1;
  let t2;
  if ($[5] !== currency || $[6] !== formatter) {
    t2 = value => currency ? formatter.number(value, {
      style: "currency",
      currency
    }) : String(value);
    $[5] = currency;
    $[6] = formatter;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const formatPrice = t2;
  const shippingCost = items.length > 0 ? shippingMethod?.cost ?? 0 : 0;
  let T0;
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== formatPrice || $[9] !== isFetching || $[10] !== items || $[11] !== onRemove || $[12] !== shippingCost || $[13] !== shippingMethod || $[14] !== t) {
    const total = getOrderValue(items.reduce(_temp, 0) + shippingCost, 1);
    const totalBeforeDiscount = getOrderValue(items.reduce(_temp2, 0) + shippingCost, 1);
    const isDiscounted = totalBeforeDiscount > total;
    let t12;
    if ($[25] !== items.length || $[26] !== shippingMethod || $[27] !== t) {
      const {
        shippingName,
        minDays,
        maxDays
      } = shippingMethod ?? {};
      const shippingDays = minDays !== undefined && maxDays !== undefined ? formatShippingDays(minDays, maxDays) : undefined;
      t12 = !shippingName ? t("shipping") : shippingDays && items.length > 0 ? t("shippingLabelWithDays", {
        name: shippingName,
        days: shippingDays
      }) : t("shippingLabel", {
        name: shippingName
      });
      $[25] = items.length;
      $[26] = shippingMethod;
      $[27] = t;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    const shippingLabel = t12;
    t9 = isFetching;
    if ($[29] !== isFetching) {
      t10 = clsx({
        "opacity-50": isFetching
      });
      $[29] = isFetching;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    let t13;
    if ($[31] !== formatPrice || $[32] !== items || $[33] !== onRemove || $[34] !== t) {
      let t14;
      if ($[36] !== formatPrice || $[37] !== onRemove || $[38] !== t) {
        t14 = t15 => {
          const {
            index,
            title,
            quantity,
            discounts,
            total: lineTotal,
            discountPercent,
            saved
          } = t15;
          const nextTier = saved > 0 ? undefined : getNextTier(discounts, quantity);
          const tiersRow = saved > 0 ? {
            label: t("discount", {
              percent: discountPercent,
              name: title
            }),
            value: `-${formatPrice(saved)}`
          } : nextTier && {
            label: t("volumeDiscountFrom", {
              count: nextTier.from
            }),
            value: ""
          };
          return <li key={index} className="relative"><dl className="flex flex-col gap-1"><SummaryRow label={`${quantity} ${title}`} value={formatPrice(lineTotal)} />{tiersRow && <SummaryRow label={<button type="button" aria-haspopup="dialog" className="cursor-pointer text-left hover:underline" onClick={() => setTiersIndex(index)}>{tiersRow.label}</button>} value={tiersRow.value} highlight={true} />}</dl><button type="button" aria-label={t("remove", {
              name: title
            })} className="absolute top-0.5 left-full ml-1 cursor-pointer md:ml-2" onClick={() => onRemove(index, 0)}><Trash className="size-3.5 text-neutral-800 md:size-4" /></button></li>;
        };
        $[36] = formatPrice;
        $[37] = onRemove;
        $[38] = t;
        $[39] = t14;
      } else {
        t14 = $[39];
      }
      t13 = items.map(t14);
      $[31] = formatPrice;
      $[32] = items;
      $[33] = onRemove;
      $[34] = t;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[40] !== formatPrice || $[41] !== shippingCost || $[42] !== shippingLabel || $[43] !== shippingMethod || $[44] !== t) {
      t14 = shippingMethod && <li><dl><SummaryRow label={shippingLabel} value={shippingCost === 0 ? t("free") : formatPrice(shippingCost)} freeShipping={shippingCost === 0} /></dl></li>;
      $[40] = formatPrice;
      $[41] = shippingCost;
      $[42] = shippingLabel;
      $[43] = shippingMethod;
      $[44] = t;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    if ($[46] !== t13 || $[47] !== t14) {
      t11 = <ul className="flex w-full flex-col gap-2">{t13}{t14}</ul>;
      $[46] = t13;
      $[47] = t14;
      $[48] = t11;
    } else {
      t11 = $[48];
    }
    t8 = "mt-2 border-t border-neutral-200 pt-2";
    T0 = SummaryRow;
    t6 = true;
    let t15;
    if ($[49] !== t) {
      t15 = t("total");
      $[49] = t;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    let t16;
    if ($[51] !== t15) {
      t16 = <span className="font-bold text-black">{t15}</span>;
      $[51] = t15;
      $[52] = t16;
    } else {
      t16 = $[52];
    }
    let t17;
    if ($[53] !== t) {
      t17 = t("vatIncluded");
      $[53] = t;
      $[54] = t17;
    } else {
      t17 = $[54];
    }
    if ($[55] !== t16 || $[56] !== t17) {
      t7 = <>{t16} {t17}</>;
      $[55] = t16;
      $[56] = t17;
      $[57] = t7;
    } else {
      t7 = $[57];
    }
    t5 = isDiscounted && <s className="mr-2 text-sm text-neutral-800">{formatPrice(totalBeforeDiscount)}</s>;
    const t18 = isDiscounted && "text-red-600";
    if ($[58] !== t18) {
      t3 = clsx("font-made text-2xl leading-normal font-black tabular-nums", t18);
      $[58] = t18;
      $[59] = t3;
    } else {
      t3 = $[59];
    }
    t4 = formatPrice(total);
    $[8] = formatPrice;
    $[9] = isFetching;
    $[10] = items;
    $[11] = onRemove;
    $[12] = shippingCost;
    $[13] = shippingMethod;
    $[14] = t;
    $[15] = T0;
    $[16] = t10;
    $[17] = t11;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[15];
    t10 = $[16];
    t11 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t12;
  if ($[60] !== t3 || $[61] !== t4) {
    t12 = <span className={t3}>{t4}</span>;
    $[60] = t3;
    $[61] = t4;
    $[62] = t12;
  } else {
    t12 = $[62];
  }
  let t13;
  if ($[63] !== t12 || $[64] !== t5) {
    t13 = <>{t5}{t12}</>;
    $[63] = t12;
    $[64] = t5;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  let t14;
  if ($[66] !== T0 || $[67] !== t13 || $[68] !== t6 || $[69] !== t7) {
    t14 = <T0 stackOnMobile={t6} label={t7} value={t13} />;
    $[66] = T0;
    $[67] = t13;
    $[68] = t6;
    $[69] = t7;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  let t15;
  if ($[71] !== t14 || $[72] !== t8) {
    t15 = <dl className={t8}>{t14}</dl>;
    $[71] = t14;
    $[72] = t8;
    $[73] = t15;
  } else {
    t15 = $[73];
  }
  let t16;
  if ($[74] !== t10 || $[75] !== t11 || $[76] !== t15 || $[77] !== t9) {
    t16 = <div aria-busy={t9} className={t10}>{t11}{t15}</div>;
    $[74] = t10;
    $[75] = t11;
    $[76] = t15;
    $[77] = t9;
    $[78] = t16;
  } else {
    t16 = $[78];
  }
  let t17;
  if ($[79] !== tiersItem?.discounts) {
    t17 = tiersItem?.discounts ?? [];
    $[79] = tiersItem?.discounts;
    $[80] = t17;
  } else {
    t17 = $[80];
  }
  const t18 = tiersItem !== undefined;
  let t19;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = open => !open && setTiersIndex(undefined);
    $[81] = t19;
  } else {
    t19 = $[81];
  }
  let t20;
  if ($[82] !== t17 || $[83] !== t18) {
    t20 = <VolumeDiscountTiersDialog discounts={t17} open={t18} className="pb-8 md:h-87 md:max-w-md md:pb-0" onOpenChange={t19} />;
    $[82] = t17;
    $[83] = t18;
    $[84] = t20;
  } else {
    t20 = $[84];
  }
  let t21;
  if ($[85] !== t16 || $[86] !== t20) {
    t21 = <>{t16}{t20}</>;
    $[85] = t16;
    $[86] = t20;
    $[87] = t21;
  } else {
    t21 = $[87];
  }
  return t21;
}
function _temp2(sum_0, item_1) {
  return sum_0 + item_1.total;
}
function _temp(sum, item_0) {
  return sum + item_0.discountedTotal;
}